import { useEffect } from 'react';
import { Link } from 'react-router';
import { platform } from '../lib/api';
import { setAppTitle, timeAgo } from '../lib/format';
import { Icon } from '../lib/Icon';
import { useLoad } from './common';

export function DashboardPage() {
  const { data, error } = useLoad(() => platform.overview());
  useEffect(() => setAppTitle('Platform admin'), []);

  return (
    <main className="page wide pa-page">
      <header className="pa-head">
        <h1>Dashboard</h1>
        <div className="actions">
          <Link className="btn primary" to="/platform-admin/sellers/new">New seller</Link>
          <Link className="btn" to="/platform-admin/sales/new">New sale</Link>
        </div>
      </header>

      {error && <p className="error" role="alert">{error}</p>}
      {!data ? (
        !error && <p className="muted">Loading…</p>
      ) : (
        <>
          <ul className="stat-grid">
            <li className="stat">
              <span className="stat-num">{data.stats.sellers}</span>
              <span>Sellers{data.stats.disabledSellers ? ` · ${data.stats.disabledSellers} disabled` : ''}</span>
            </li>
            <li className="stat">
              <span className="stat-num">{data.stats.sales}</span>
              <span>Sales · {data.stats.publishedSales} published</span>
            </li>
            <li className="stat">
              <span className="stat-num">{data.stats.listings}</span>
              <span>Listings</span>
            </li>
            <li className="stat">
              <span className="stat-num">{data.stats.inquiries}</span>
              <span>Inquiries{data.stats.newInquiries ? ` · ${data.stats.newInquiries} new` : ''}</span>
            </li>
          </ul>

          {data.stats.newSellerRequests > 0 && (
            <p className="notice small">
              <Icon name="inbox" /> {data.stats.newSellerRequests} new Start Selling request{data.stats.newSellerRequests === 1 ? '' : 's'}.{' '}
              <Link to="/platform-admin/requests">Review</Link>
            </p>
          )}

          <p className="muted small" data-testid="listing-time">
            {data.stats.listingSeconds
              ? `Typical time to list an item: ${formatDuration(data.stats.listingSeconds.median)} (median of ${data.stats.listingSeconds.items} in the last 30 days, first photo to publish).`
              : 'Typical time to list an item will show here once items are published.'}
          </p>

          {!data.emailEnabled && (
            <p className="notice small"><Icon name="info" /> Email is off, so setup links must be sent by hand.</p>
          )}

          <section className="card">
            <h2>Recent activity</h2>
            {data.activity.length === 0 ? (
              <p className="muted">Nothing yet. Create a seller to get started.</p>
            ) : (
              <ul className="activity">
                {data.activity.map((a) => (
                  <li key={a.id}>
                    <Link to={a.targetType === 'sale' ? `/platform-admin/sales/${a.targetId}` : `/platform-admin/sellers/${a.targetId}`}>
                      {a.summary}
                    </Link>
                    <span className="muted small">
                      {a.actorName ?? 'System'} · {timeAgo(a.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </main>
  );
}

/** "45 sec", "2 min 5 sec", "3 h 10 min". */
function formatDuration(secs: number): string {
  if (secs < 60) return `${secs} sec`;
  if (secs < 3600) return `${Math.floor(secs / 60)} min${secs % 60 ? ` ${secs % 60} sec` : ''}`;
  return `${Math.floor(secs / 3600)} h ${Math.floor((secs % 3600) / 60)} min`;
}
